import {
  Approval,
  ApprovalDecision,
  AttentionItem,
  BypassState,
  DeviceReference,
  MaintenanceWindow,
  MaintenanceWindowFilters,
  WindowStatus,
} from '@totem/components/maintenanceWindowsV2/types';

export const HOUR_MS = 60 * 60 * 1000;
export const DAY_MS = 24 * HOUR_MS;

// Auto-approval (the creator is also an approver) is recorded within a few milliseconds of the request.
const AUTO_APPROVAL_MS = 60 * 1000;

export const STATUS_LABELS: Record<WindowStatus, string> = {
  active: 'Active',
  overdue: 'Not closed',
  awaitingApproval: 'Awaiting approval',
  bypassRejected: 'Bypass rejected',
  scheduled: 'Scheduled',
  closed: 'Closed',
};

export const BYPASS_LABELS: Record<BypassState, string> = {
  notRequired: 'Not required',
  pending: 'Awaiting approval',
  rejected: 'Rejected',
  approved: 'Approved',
  open: 'Overlay open',
  notCreated: 'Overlay not created',
  notRemoved: 'Overlay may still be open',
  closed: 'Closed with window',
};

export const DECISION_LABELS: Record<ApprovalDecision, string> = {
  pending: 'Pending',
  approved: 'Approved',
  autoApproved: 'Auto-approved',
  rejected: 'Rejected',
  notRequired: 'Not required',
};

// Go zero times and missing values both mean "not set".
export const isSetDate = (value: string) =>
  Boolean(value) && !value.startsWith('0001-') && !isNaN(Date.parse(value));

export const toTime = (value: string) =>
  isSetDate(value) ? new Date(value).getTime() : NaN;

export const startTime = (win: MaintenanceWindow) =>
  toTime(win.details.scheduledStartTime);

export const endTime = (win: MaintenanceWindow) =>
  toTime(win.details.scheduledEndTime);

export const isClosed = (win: MaintenanceWindow) =>
  (win.details.status ?? '').toLowerCase() === 'closed';

const approvals = (win: MaintenanceWindow): Approval[] => win.approvals ?? [];

const overlayCount = (win: MaintenanceWindow) =>
  (win.overlayNetworkBypasses ?? []).length;

const hasRejection = (win: MaintenanceWindow) =>
  approvals(win).some((approval) => approval.status === 'Rejected');

export const getWindowStatus = (
  win: MaintenanceWindow,
  now: number,
): WindowStatus => {
  if (isClosed(win)) {
    return 'closed';
  }
  if (now < startTime(win)) {
    if (win.details.requiresBypass && !win.details.bypassApproved) {
      return hasRejection(win) ? 'bypassRejected' : 'awaitingApproval';
    }
    return 'scheduled';
  }
  if (now <= endTime(win)) {
    return 'active';
  }
  // Past its end but never closed by the scheduler: alerts stay suppressed and any overlay is not removed.
  return 'overdue';
};

export const getBypassState = (
  win: MaintenanceWindow,
  now: number,
): BypassState => {
  if (!win.details.requiresBypass) {
    return 'notRequired';
  }
  if (!win.details.bypassApproved) {
    return hasRejection(win) ? 'rejected' : 'pending';
  }
  const status = getWindowStatus(win, now);
  const created = overlayCount(win) > 0;
  switch (status) {
    case 'scheduled':
      return 'approved';
    case 'active':
      return created ? 'open' : 'notCreated';
    case 'overdue':
      return created ? 'notRemoved' : 'notCreated';
    default:
      return created ? 'closed' : 'notCreated';
  }
};

export const isAutoApproval = (win: MaintenanceWindow, approval: Approval) =>
  approval.approved &&
  approval.approver.toLowerCase() === (win.createdBy ?? '').toLowerCase() &&
  toTime(approval.approvedAt) - toTime(approval.requestedAt) < AUTO_APPROVAL_MS;

// Any one approver approves the bypass; the remaining requests then no longer need a decision.
export const getApprovalDecision = (
  win: MaintenanceWindow,
  approval: Approval,
): ApprovalDecision => {
  if (approval.status === 'Approved') {
    return isAutoApproval(win, approval) ? 'autoApproved' : 'approved';
  }
  if (approval.status === 'Rejected') {
    return 'rejected';
  }
  return win.details.bypassApproved || isClosed(win)
    ? 'notRequired'
    : 'pending';
};

export const approvalResponseMs = (approval: Approval) => {
  const response = toTime(approval.approvedAt) - toTime(approval.requestedAt);
  return isNaN(response) || response < 0 ? NaN : response;
};

export const isPendingForUser = (
  win: MaintenanceWindow,
  email: string,
  now: number,
) => {
  const status = getWindowStatus(win, now);
  if (status === 'closed' || status === 'overdue') {
    return false;
  }
  return approvals(win).some(
    (approval) =>
      approval.approver.toLowerCase() === email.toLowerCase() &&
      getApprovalDecision(win, approval) === 'pending',
  );
};

export const isApproverOn = (win: MaintenanceWindow, email: string) =>
  approvals(win).some(
    (approval) => approval.approver.toLowerCase() === email.toLowerCase(),
  );

export const getBuildingNames = (win: MaintenanceWindow) => [
  ...new Set(
    (win.devices ?? []).map((dev) => dev.building?.name).filter(Boolean),
  ),
];

export const getControlSystemNames = (win: MaintenanceWindow) =>
  [
    ...new Set(
      (win.devices ?? []).map((dev) => dev.controlSystem?.name).filter(Boolean),
    ),
  ].sort((left, right) => left.localeCompare(right));

export const getBuildingLabel = (win: MaintenanceWindow) => {
  const names = getBuildingNames(win);
  if (names.length === 0) {
    return '-';
  }
  return names.length === 1 ? names[0] : `${names.length} buildings`;
};

export const getTicketId = (win: MaintenanceWindow) =>
  (win.keys ?? []).find(
    (key) => key.system === 'Ticketing' && key.instance === 'IBD',
  )?.key;

export const getTicketIds = (win: MaintenanceWindow) =>
  (win.keys ?? [])
    .filter((key) => key.system === 'Ticketing' && key.instance === 'IBD')
    .map((key) => key.key);

export const durationHours = (win: MaintenanceWindow) => {
  const hours = (endTime(win) - startTime(win)) / HOUR_MS;
  return isNaN(hours) || hours < 0 ? 0 : hours;
};

export const formatDuration = (ms: number) => {
  if (isNaN(ms)) {
    return '-';
  }
  const totalMinutes = Math.round(Math.abs(ms) / 60000);
  const days = Math.floor(totalMinutes / 1440);
  const hours = Math.floor((totalMinutes % 1440) / 60);
  const minutes = totalMinutes % 60;
  if (days > 0) {
    return hours > 0 ? `${days}d ${hours}h` : `${days}d`;
  }
  if (hours > 0) {
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return `${minutes}m`;
};

export const formatTime = (value: string) =>
  isSetDate(value)
    ? new Date(value).toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

export const formatDateTime = (value: string) =>
  isSetDate(value)
    ? new Date(value).toLocaleString([], {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '-';

export const formatDay = (time: number) =>
  new Date(time).toLocaleDateString([], {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
  });

export const formatSchedule = (win: MaintenanceWindow) => {
  const start = startTime(win);
  const end = endTime(win);
  if (isNaN(start)) {
    return '-';
  }
  const sameDay =
    new Date(start).toDateString() === new Date(end).toDateString();
  return sameDay
    ? `${formatDateTime(win.details.scheduledStartTime)} – ${formatTime(win.details.scheduledEndTime)}`
    : `${formatDateTime(win.details.scheduledStartTime)} – ${formatDateTime(win.details.scheduledEndTime)}`;
};

// A window matches when any of its devices is in each selected region / building / control system.
export const matchesFilters = (
  win: MaintenanceWindow,
  filters: MaintenanceWindowFilters,
) => {
  const devices = win.devices ?? [];
  const matches = (ids: string[], pick: (dev: DeviceReference) => string) =>
    ids.length === 0 || devices.some((dev) => ids.includes(pick(dev)));
  return (
    matches(filters.regionIds, (dev) => dev.region?.id) &&
    matches(filters.buildingIds, (dev) => dev.building?.id) &&
    matches(filters.controlSystemIds, (dev) => dev.controlSystem?.id)
  );
};

export const median = (values: number[]) => {
  const sorted = values
    .filter((value) => !isNaN(value))
    .sort((left, right) => left - right);
  if (sorted.length === 0) {
    return NaN;
  }
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
};

export const percentile = (values: number[], fraction: number) => {
  const sorted = values
    .filter((value) => !isNaN(value))
    .sort((left, right) => left - right);
  if (sorted.length === 0) {
    return NaN;
  }
  return sorted[
    Math.min(sorted.length - 1, Math.ceil(fraction * sorted.length) - 1)
  ];
};

// Windows that started inside the history window (closed or not).
export const inLookback = (
  win: MaintenanceWindow,
  now: number,
  lookbackDays: number,
) => {
  const start = startTime(win);
  return start >= now - lookbackDays * DAY_MS && start <= now;
};

export const buildAttentionItems = (
  windows: MaintenanceWindow[],
  now: number,
  lookbackDays: number,
): AttentionItem[] => {
  const items: AttentionItem[] = [];
  let criticalSuppressed = 0;

  windows.forEach((win) => {
    const status = getWindowStatus(win, now);
    const bypass = getBypassState(win, now);
    const building = getBuildingLabel(win);
    const name = win.details.reason || 'Maintenance window';
    const startsIn = startTime(win) - now;

    if (status === 'overdue') {
      items.push({
        key: `${win.id}-overdue`,
        severity: 'critical',
        title: `Not closed ${formatDuration(now - endTime(win))} after its scheduled end`,
        detail: `${building} · ${name} · alerts are still suppressed${bypass === 'notRemoved' ? ' and the overlay bypass was not removed' : ''}`,
        windowId: win.id,
      });
    }
    if (status === 'active' && bypass === 'notCreated') {
      items.push({
        key: `${win.id}-not-created`,
        severity: 'critical',
        title: 'Bypass approved but no overlay network was created',
        detail: `${building} · ${name} · remote access is not available`,
        windowId: win.id,
      });
    }
    if (status === 'awaitingApproval' && startsIn < 2 * DAY_MS) {
      items.push({
        key: `${win.id}-approval`,
        severity: 'warning',
        title: `Starts in ${formatDuration(startsIn)} without bypass approval`,
        detail: `${building} · ${name}`,
        windowId: win.id,
      });
    }
    if (status === 'bypassRejected') {
      items.push({
        key: `${win.id}-rejected`,
        severity: 'warning',
        title: 'Bypass rejected for an upcoming window',
        detail: `${building} · ${name} · starts ${formatDateTime(win.details.scheduledStartTime)}`,
        windowId: win.id,
      });
    }
    if (status !== 'closed' && (win.devices ?? []).length === 0) {
      items.push({
        key: `${win.id}-no-devices`,
        severity: 'info',
        title: 'No devices in the window',
        detail: `${name} · nothing will be suppressed`,
        windowId: win.id,
      });
    }
    if (
      status !== 'closed' &&
      status !== 'overdue' &&
      !win.details.siteContactName &&
      !win.details.siteContactPhone
    ) {
      items.push({
        key: `${win.id}-contact`,
        severity: 'info',
        title: 'No site contact',
        detail: `${building} · ${name} · ${formatDateTime(win.details.scheduledStartTime)}`,
        windowId: win.id,
      });
    }
    if (inLookback(win, now, lookbackDays)) {
      criticalSuppressed += (win.blockedEvents ?? []).filter(
        (event) => event.severity >= 5,
      ).length;
    }
  });

  if (criticalSuppressed > 0) {
    items.push({
      key: 'critical-suppressed',
      severity: 'info',
      title: `${criticalSuppressed} critical alert${criticalSuppressed === 1 ? '' : 's'} suppressed in the last ${lookbackDays} days`,
      detail: 'Worth a second look: devices outside the work may have alarmed',
    });
  }

  const order = { critical: 0, warning: 1, info: 2 };
  return items.sort(
    (left, right) => order[left.severity] - order[right.severity],
  );
};
